// Ред-рецепта (неразделена) в решетката „Изписване": име + „Рецепта" и под тях
// колко порции стигат КЪМ ДАТАТА (както продуктовият ред казва наличността си),
// порции · Ед. цена (себестойността на ЕДНА порция) · Нето · Бруто по
// партидите/FIFO на съставките — същата основа като продуктовите редове — и
// бележка. Не стига ли нещо, под реда е лентата „Не стига за N порции" със
// съставките и ходовете „Намали на N порции" / „Раздели на продукти"
// (WriteoffShortageBand). Партида няма: рецептата се изписва винаги по FIFO.
import { SplitCellsOutlined } from "@ant-design/icons";
import { TruncatedText } from "../../components/list/TruncatedText";
import { SHEET_META_TEXT, SHEET_NAME_CELL, SHEET_NAME_TEXT, SHEET_ROW } from "../../components/list/density";
import { useTranslation } from "react-i18next";
import type { RecipeRowStock } from "./useWriteoffStock";
import { WriteoffShortageBand } from "./WriteoffShortageBand";
import { RecipeFitsText } from "./WriteoffStockTexts";
import {
  DashCell,
  MoneyCell,
  NOTE_INPUT_CLASS,
  QTY_INPUT_CLASS,
  RemoveRowButton,
  RowCell,
  RowIndexCell,
} from "./WriteoffGridCells";
import {
  LINE_GRID_CLASS,
  cellId,
  formatPortionsInput,
  parseDecimal,
  type WriteoffLineDraft,
} from "./writeoffWorksheetShared";

type Props = {
  line: WriteoffLineDraft;
  index: number;
  // undefined — съставките още се зареждат
  stock: RecipeRowStock | undefined;
  onPatchLine: (key: number, patch: Partial<WriteoffLineDraft>) => void;
  onRequestRemove: (key: number) => void;
  onSplitRecipe: (key: number) => void;
  onFitPortions: (key: number, portions: number) => void;
};

export function WriteoffRecipeRow({
  line,
  index,
  stock,
  onPatchLine,
  onRequestRemove,
  onSplitRecipe,
  onFitPortions,
}: Props) {
  const { t } = useTranslation();
  const filled = parseDecimal(line.quantity) > 0;
  const short = (stock?.shortages.length ?? 0) > 0;
  const values = stock?.values;
  return (
    <>
      <div
        className={`${LINE_GRID_CLASS} ${SHEET_ROW} items-stretch border-b border-shade-2 ${filled ? "bg-panel" : "bg-surface"}`}
      >
        <RowIndexCell index={index} />
        {/* РЕЦЕПТА: име + бадж, под тях — колко порции стигат */}
        <div className={`${SHEET_NAME_CELL} border-r border-shade-2`}>
          <span className="flex min-w-0 items-center gap-2.5">
            <TruncatedText text={line.recipeName ?? "—"} className={SHEET_NAME_TEXT} />
            {/* баджът е 16px — да не вдига двата реда над 34px */}
            <span className="shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] text-[11px] font-semibold leading-[14px] text-brand-deep-ink">
              {t("inventoryPage.recipe")}
            </span>
          </span>
          <span className={`${SHEET_META_TEXT} ${short ? "font-bold text-danger-ink" : "text-muted"}`}>
            <RecipeFitsText stock={stock} />
          </span>
        </div>
        {/* ПОРЦИИ (множител) — плоска клетка */}
        <RowCell>
          <input
            id={cellId(line.key, 0)}
            data-rk={line.key}
            data-c={0}
            value={line.quantity}
            placeholder={t("inventoryPage.portionsPh")}
            inputMode="decimal"
            aria-invalid={short || undefined}
            onChange={(e) => onPatchLine(line.key, { quantity: e.target.value.replace(/[^0-9.,]/g, "") })}
            onFocus={(e) => e.target.select()}
            onBlur={() => {
              const parsed = parseDecimal(line.quantity);
              if (line.quantity !== "" && parsed > 0)
                onPatchLine(line.key, { quantity: formatPortionsInput(parsed) });
            }}
            className={`${QTY_INPUT_CLASS} ${short ? "!border-danger" : ""}`}
          />
        </RowCell>
        {/* Партида — няма (рецептата се изписва по FIFO) */}
        <DashCell />
        {/* ЕД. ЦЕНА = себестойността на една порция; Нето/Бруто = × порциите */}
        <MoneyCell value={values?.unitPrice ?? null} unitPrice />
        <MoneyCell value={values?.netSum ?? null} />
        <MoneyCell value={values?.grossSum ?? null} strong />
        <RowCell>
          <input
            id={cellId(line.key, 2)}
            data-rk={line.key}
            data-c={2}
            value={line.note}
            placeholder={t("inventoryPage.notePh")}
            onChange={(e) => onPatchLine(line.key, { note: e.target.value })}
            className={NOTE_INPUT_CLASS}
          />
        </RowCell>
        {/* Раздели + ✕ — компактно в тясната 40px колона */}
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => onSplitRecipe(line.key)}
            aria-label={t("inventoryPage.splitToProducts")}
            title={t("inventoryPage.splitToProducts")}
            tabIndex={-1}
            className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-brand-ink hover:text-brand-deep-ink"
          >
            <SplitCellsOutlined className="text-[13px]" />
          </button>
          <RemoveRowButton
            onClick={() => onRequestRemove(line.key)}
            hint={t("inventoryPage.confirmDeleteRowTitle")}
            compact
          />
        </div>
      </div>
      {short && stock && (
        <WriteoffShortageBand
          line={line}
          shortages={stock.shortages}
          fittingPortions={stock.fittingPortions}
          onFitPortions={onFitPortions}
          onSplitRecipe={onSplitRecipe}
          variant="row"
        />
      )}
    </>
  );
}

export default WriteoffRecipeRow;
